import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "@/pages/layoutChrome";
import LaboralFlujoOperativo from "@/components/laboral/LaboralFlujoOperativo";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  Briefcase,
  ClipboardList,
  FileWarning,
  FolderOpen,
  Info,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Users,
  GitCompareArrows,
} from "lucide-react";
import { toast } from "sonner";
import DespachoOperationalSummary from "@/components/despacho/DespachoOperationalSummary";

import { DATOS_MAESTROS_CHECK_ID_PATH } from "@/modules/datosMaestros/datosMaestrosHub";

const LABORAL_CHECK_ID_PATH = DATOS_MAESTROS_CHECK_ID_PATH;

const CONTEXT_LABEL = "Asistente Laboral y REPSE";

function CountBadge({ value, tone = "neutral" }) {
  const tones = {
    critical: "bg-rose-100 text-rose-900 border-rose-200",
    warn: "bg-amber-100 text-amber-900 border-amber-200",
    info: "bg-sky-100 text-sky-900 border-sky-200",
    neutral: "bg-slate-100 text-slate-800 border-slate-200",
  };
  return (
    <span
      className={`inline-flex min-w-[2rem] justify-center rounded-sm border px-2 py-0.5 text-sm font-bold tabular-nums ${tones[tone] || tones.neutral}`}
    >
      {value ?? 0}
    </span>
  );
}

function SectionCard({ title, icon: Icon, tone, children, testid }) {
  const tones = {
    critical: "border-rose-200 bg-rose-50/40",
    warn: "border-amber-200 bg-amber-50/40",
    info: "border-sky-200 bg-sky-50/40",
  };
  return (
    <section
      className={`rounded-sm border p-4 space-y-3 ${tones[tone] || "border-slate-200 bg-white"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        {Icon ? <Icon className="w-4 h-4 text-[#0F2D5C]" aria-hidden /> : null}
        <h2 className="text-sm font-bold text-[#0F2D5C]">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function VencimientoCounters({ contadores }) {
  const rows = [
    { key: "vencidos", label: "Vencidos", tone: "critical" },
    { key: "7_dias", label: "7 días", tone: "critical" },
    { key: "15_dias", label: "15 días", tone: "warn" },
    { key: "30_dias", label: "30 días", tone: "warn" },
  ];
  return (
    <div className="flex flex-wrap gap-3">
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-2 text-xs">
          <span className="text-slate-600">{r.label}</span>
          <CountBadge value={contadores?.[r.key] ?? 0} tone={r.tone} />
        </div>
      ))}
    </div>
  );
}

function TrabajadoresMiniTable({ items, extraColumns = [] }) {
  if (!items?.length) {
    return <p className="text-sm text-slate-600">Sin registros en esta categoría.</p>;
  }
  return (
    <div className="overflow-x-auto border rounded-sm bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>CURP</TableHead>
            <TableHead>RFC</TableHead>
            <TableHead>NSS</TableHead>
            {extraColumns.map((col) => (
              <TableHead key={col.key}>{col.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="font-medium">{row.nombre_completo || "—"}</TableCell>
              <TableCell className="font-mono text-xs">{row.curp || "—"}</TableCell>
              <TableCell className="font-mono text-xs">{row.rfc || "—"}</TableCell>
              <TableCell className="font-mono text-xs">{row.nss || "—"}</TableCell>
              {extraColumns.map((col) => (
                <TableCell key={col.key} className="text-xs">
                  {col.render ? col.render(row) : row[col.key] || "—"}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Dashboard gerencial Laboral/REPSE — alertas de solo lectura (Fase 1).
 */
export default function DashboardLaboral() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    api
      .get("/clients")
      .then(({ data: rows }) => setClients(Array.isArray(rows) ? rows : []))
      .catch(() => setClients([]));
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!clientId) {
      setData(null);
      setLoadError("");
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const { data: payload } = await api.get("/laboral/dashboard", {
        params: { client_id: clientId },
        timeout: 60000,
      });
      setData(payload);
    } catch (e) {
      setData(null);
      const message = formatApiError(e.response?.data?.detail) || "No se pudo cargar el dashboard laboral";
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const confronta = data?.confronta_sat_laboral;
  const confrontaResumen = confronta?.resumen_ejecutivo;

  const resumen = useMemo(() => {
    if (!data) return { critical: 0, warn: 0, info: 0 };
    const satCrit =
      (confrontaResumen?.trabajadores_no_registrados || 0) +
      (confrontaResumen?.diferencias_rfc || 0) +
      (confrontaResumen?.diferencias_curp || 0) +
      (confrontaResumen?.diferencias_sdi || 0);
    const critical =
      (data.trabajadores_sin_nss?.total || 0) +
      (data.trabajadores_sin_rfc?.total || 0) +
      (data.expedientes_incompletos?.total || 0) +
      (data.contratos_por_vencer?.contadores?.vencidos || 0) +
      (data.obras_repse_por_vencer?.contadores?.vencidos || 0) +
      satCrit;
    const warn =
      (data.contratos_por_vencer?.contadores?.["7_dias"] || 0) +
      (data.contratos_por_vencer?.contadores?.["15_dias"] || 0) +
      (data.contratos_por_vencer?.contadores?.["30_dias"] || 0) +
      (data.obras_repse_por_vencer?.contadores?.["7_dias"] || 0) +
      (data.obras_repse_por_vencer?.contadores?.["15_dias"] || 0) +
      (data.obras_repse_por_vencer?.contadores?.["30_dias"] || 0) +
      (data.cuatrimestrales_pendientes?.total || 0) +
      (confrontaResumen?.sin_nomina_reciente || 0);
    const info = data.periodo_cuatrimestral?.label ? 1 : 0;
    return { critical, warn, info };
  }, [data, confrontaResumen]);

  const clientLabel = clients.find((c) => c.id === clientId)?.razon_social || "";

  return (
    <div className="space-y-6" data-testid="dashboard-laboral">
      <PageHeader
        title="Panel de control laboral"
        subtitle="Alertas operativas y de cumplimiento — solo lectura, sin modificar datos."
        breadcrumb={[CONTEXT_LABEL, "Panel"]}
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadDashboard}
            disabled={loading || !clientId}
            data-testid="dashboard-laboral-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        }
      />

      <LaboralFlujoOperativo>
        <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Cliente</label>
            <Select value={clientId || undefined} onValueChange={setClientId}>
              <SelectTrigger data-testid="dashboard-laboral-client">
                <SelectValue placeholder="Seleccione cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.razon_social || c.rfc || c.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {data?.generated_at ? (
            <p className="text-xs text-slate-500">
              Actualizado: {new Date(data.generated_at).toLocaleString("es-MX")}
              {clientLabel ? ` · ${clientLabel}` : ""}
            </p>
          ) : null}
        </div>

        {!clientId ? (
          <div className="rounded-sm border border-dashed p-8 text-center text-slate-600">
            Seleccione un cliente para ver las alertas del dashboard.
          </div>
        ) : loading && !data ? (
          <div className="flex items-center justify-center py-16 text-slate-600">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Cargando alertas…
          </div>
        ) : loadError ? (
          <div
            className="rounded-sm border border-amber-200 bg-amber-50 p-8 text-center space-y-3"
            data-testid="dashboard-laboral-error"
          >
            <p className="text-sm text-amber-950">{loadError}</p>
            <Button type="button" variant="outline" size="sm" onClick={loadDashboard} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Reintentar
            </Button>
          </div>
        ) : data ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3">
                <div className="flex items-center gap-2 text-rose-900 text-xs font-bold uppercase">
                  <ShieldAlert className="w-4 h-4" />
                  Alertas críticas
                </div>
                <p className="text-2xl font-bold text-rose-950 mt-1">{resumen.critical}</p>
              </div>
              <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="flex items-center gap-2 text-amber-900 text-xs font-bold uppercase">
                  <AlertTriangle className="w-4 h-4" />
                  Preventivas
                </div>
                <p className="text-2xl font-bold text-amber-950 mt-1">{resumen.warn}</p>
              </div>
              <div className="rounded-sm border border-sky-200 bg-sky-50 px-4 py-3">
                <div className="flex items-center gap-2 text-sky-900 text-xs font-bold uppercase">
                  <Info className="w-4 h-4" />
                  Periodo cuatrimestral
                </div>
                <p className="text-sm font-semibold text-sky-950 mt-2">
                  {data.periodo_cuatrimestral?.label || "—"}
                </p>
              </div>
            </div>

            <SectionCard
              title="Trabajadores sin NSS validado"
              icon={Users}
              tone="critical"
              testid="dash-alert-nss"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CountBadge value={data.trabajadores_sin_nss?.total} tone="critical" />
                <Link
                  to={LABORAL_CHECK_ID_PATH}
                  className="text-xs font-semibold text-[#0F2D5C] hover:underline"
                >
                  Ir a validación NSS →
                </Link>
              </div>
              <TrabajadoresMiniTable
                items={data.trabajadores_sin_nss?.items}
                extraColumns={[{ key: "motivo", label: "Motivo", render: (r) => r.motivo || "—" }]}
              />
              {data.trabajadores_sin_nss?.truncated ? (
                <p className="text-xs text-slate-500">Listado resumido (primeros 25).</p>
              ) : null}
            </SectionCard>

            <SectionCard
              title="Trabajadores sin RFC validado"
              icon={FileWarning}
              tone="critical"
              testid="dash-alert-rfc"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CountBadge value={data.trabajadores_sin_rfc?.total} tone="critical" />
                <Link
                  to={LABORAL_CHECK_ID_PATH}
                  className="text-xs font-semibold text-[#0F2D5C] hover:underline"
                >
                  Ir a validación RFC →
                </Link>
              </div>
              <TrabajadoresMiniTable
                items={data.trabajadores_sin_rfc?.items}
                extraColumns={[{ key: "motivo", label: "Motivo", render: (r) => r.motivo || "—" }]}
              />
            </SectionCard>

            <SectionCard
              title="Expedientes incompletos"
              icon={FolderOpen}
              tone="critical"
              testid="dash-alert-expedientes"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CountBadge value={data.expedientes_incompletos?.total} tone="critical" />
                <Link to="/expedientes-laborales" className="text-xs font-semibold text-[#0F2D5C] hover:underline">
                  Ver expedientes →
                </Link>
              </div>
              <TrabajadoresMiniTable
                items={data.expedientes_incompletos?.items}
                extraColumns={[
                  {
                    key: "documentos_faltantes",
                    label: "Falta",
                    render: (r) => (r.documentos_faltantes || []).join(", ") || "—",
                  },
                ]}
              />
            </SectionCard>

            <SectionCard
              title="Contratos laborales próximos a vencer"
              icon={ClipboardList}
              tone="warn"
              testid="dash-alert-contratos"
            >
              <VencimientoCounters contadores={data.contratos_por_vencer?.contadores} />
              <div className="overflow-x-auto border rounded-sm bg-white">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Trabajador</TableHead>
                      <TableHead>Fin contrato</TableHead>
                      <TableHead>Categoría</TableHead>
                      <TableHead>Días</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.contratos_por_vencer?.items || []).map((row) => (
                      <TableRow key={`${row.id}-${row.fecha_fin}`}>
                        <TableCell>{row.nombre_completo || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{row.fecha_fin}</TableCell>
                        <TableCell>{row.categoria}</TableCell>
                        <TableCell>{row.dias_restantes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </SectionCard>

            <SectionCard
              title="Obras REPSE próximas a vencer"
              icon={Briefcase}
              tone="warn"
              testid="dash-alert-obras"
            >
              <VencimientoCounters contadores={data.obras_repse_por_vencer?.contadores} />
              <div className="overflow-x-auto border rounded-sm bg-white">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contrato</TableHead>
                      <TableHead>Obra</TableHead>
                      <TableHead>Fin</TableHead>
                      <TableHead>Categoría</TableHead>
                      <TableHead>Asignados</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.obras_repse_por_vencer?.items || []).map((row) => (
                      <TableRow key={`${row.id}-${row.fecha_fin}`}>
                        <TableCell className="font-mono text-xs">{row.numero_contrato}</TableCell>
                        <TableCell>{row.razon_social_obra}</TableCell>
                        <TableCell className="font-mono text-xs">{row.fecha_fin}</TableCell>
                        <TableCell>{row.categoria}</TableCell>
                        <TableCell>{row.trabajadores_asignados ?? 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Link to="/control-repse" className="text-xs font-semibold text-[#0F2D5C] hover:underline">
                Ir a Control REPSE →
              </Link>
            </SectionCard>

            {confronta ? (
              <>
                <SectionCard
                  title="Confronta SAT ↔ Laboral"
                  icon={GitCompareArrows}
                  tone="info"
                  testid="dash-confronta-sat-laboral"
                >
                  <p className="text-xs text-slate-600">
                    Cruce de CFDI de nómina ya almacenados (Visor SAT) contra el catálogo laboral — solo lectura.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {[
                      { key: "trabajadores_no_registrados", label: "Sin catálogo", tone: "critical" },
                      { key: "diferencias_rfc", label: "RFC", tone: "critical" },
                      { key: "diferencias_curp", label: "CURP", tone: "critical" },
                      { key: "diferencias_sdi", label: "SDI", tone: "critical" },
                      { key: "sin_nomina_reciente", label: "Sin nómina", tone: "warn" },
                    ].map((item) => (
                      <div
                        key={item.key}
                        className="rounded-sm border bg-white px-3 py-2 text-center"
                      >
                        <p className="text-[10px] uppercase text-slate-500 font-semibold">{item.label}</p>
                        <CountBadge value={confrontaResumen?.[item.key] ?? 0} tone={item.tone} />
                      </div>
                    ))}
                  </div>
                  {confronta.meta?.cfdi_nomina_analizados != null ? (
                    <p className="text-[11px] text-slate-500">
                      CFDI nómina analizados: {confronta.meta.cfdi_nomina_analizados}
                      {confronta.meta.tiempo_ms != null ? ` · ${confronta.meta.tiempo_ms} ms` : ""}
                      {confronta.meta.sin_nomina_umbral_dias != null
                        ? ` · umbral sin nómina: ${confronta.meta.sin_nomina_umbral_dias} días`
                        : ""}
                    </p>
                  ) : null}
                </SectionCard>

                <SectionCard
                  title="Trabajadores en CFDI nómina sin catálogo laboral"
                  icon={ShieldAlert}
                  tone="critical"
                  testid="dash-confronta-no-registrados"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CountBadge
                      value={confronta.trabajadores_no_registrados?.total}
                      tone="critical"
                    />
                    <Link
                      to="/catalogo-trabajadores"
                      className="text-xs font-semibold text-[#0F2D5C] hover:underline"
                    >
                      Ir a catálogo de trabajadores →
                    </Link>
                  </div>
                  {confronta.trabajadores_no_registrados?.periodo_detectado?.desde ? (
                    <p className="text-xs text-slate-600">
                      Periodo detectado:{" "}
                      {confronta.trabajadores_no_registrados.periodo_detectado.desde}
                      {confronta.trabajadores_no_registrados.periodo_detectado.hasta
                        ? ` — ${confronta.trabajadores_no_registrados.periodo_detectado.hasta}`
                        : ""}
                    </p>
                  ) : null}
                  <div className="overflow-x-auto border rounded-sm bg-white">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nombre (CFDI)</TableHead>
                          <TableHead>RFC</TableHead>
                          <TableHead>CURP</TableHead>
                          <TableHead>Último pago</TableHead>
                          <TableHead>Recibos</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(confronta.trabajadores_no_registrados?.items || []).map((row) => (
                          <TableRow key={row.rfc}>
                            <TableCell>{row.nombre_trabajador || "—"}</TableCell>
                            <TableCell className="font-mono text-xs">{row.rfc || "—"}</TableCell>
                            <TableCell className="font-mono text-xs">{row.curp || "—"}</TableCell>
                            <TableCell className="font-mono text-xs">
                              {row.ultima_fecha_pago || "—"}
                            </TableCell>
                            <TableCell>{row.cfdi_count ?? 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </SectionCard>

                <SectionCard
                  title="Trabajadores activos sin CFDI de nómina reciente"
                  icon={AlertTriangle}
                  tone="warn"
                  testid="dash-confronta-sin-nomina"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CountBadge value={confronta.sin_nomina_reciente?.total} tone="warn" />
                    <Link
                      to="/catalogo-trabajadores"
                      className="text-xs font-semibold text-[#0F2D5C] hover:underline"
                    >
                      Control de nóminas (catálogo) →
                    </Link>
                  </div>
                  <p className="text-xs text-slate-600">
                    Alerta preventiva: sin recibos en los últimos{" "}
                    {confronta.sin_nomina_reciente?.umbral_dias ?? 60} días (no implica baja automática).
                  </p>
                  <TrabajadoresMiniTable
                    items={confronta.sin_nomina_reciente?.items}
                    extraColumns={[
                      {
                        key: "ultima_fecha_pago",
                        label: "Último CFDI",
                        render: (r) => r.ultima_fecha_pago || "Sin registro",
                      },
                      {
                        key: "dias_sin_nomina",
                        label: "Días sin nómina",
                        render: (r) =>
                          r.dias_sin_nomina != null ? String(r.dias_sin_nomina) : "—",
                      },
                    ]}
                  />
                </SectionCard>

                <SectionCard
                  title="Diferencias RFC (catálogo vs CFDI nómina)"
                  icon={FileWarning}
                  tone="critical"
                  testid="dash-confronta-rfc"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CountBadge value={confronta.diferencias_rfc?.total} tone="critical" />
                    <Link
                      to={LABORAL_CHECK_ID_PATH}
                      className="text-xs font-semibold text-[#0F2D5C] hover:underline"
                    >
                      Ir a validación RFC →
                    </Link>
                  </div>
                  <TrabajadoresMiniTable
                    items={confronta.diferencias_rfc?.items}
                    extraColumns={[
                      { key: "tipo", label: "Tipo", render: (r) => r.tipo || "—" },
                      {
                        key: "rfc_cfdi",
                        label: "RFC CFDI",
                        render: (r) => r.rfc_cfdi || "—",
                      },
                    ]}
                  />
                </SectionCard>

                <SectionCard
                  title="Diferencias CURP (catálogo vs CFDI nómina)"
                  icon={FileWarning}
                  tone="critical"
                  testid="dash-confronta-curp"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CountBadge value={confronta.diferencias_curp?.total} tone="critical" />
                    <Link
                      to="/catalogo-trabajadores"
                      className="text-xs font-semibold text-[#0F2D5C] hover:underline"
                    >
                      Ir a catálogo →
                    </Link>
                  </div>
                  {confronta.diferencias_curp?.limitacion ? (
                    <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                      {confronta.diferencias_curp.limitacion}
                    </p>
                  ) : null}
                  <TrabajadoresMiniTable
                    items={confronta.diferencias_curp?.items}
                    extraColumns={[
                      {
                        key: "curp_cfdi",
                        label: "CURP CFDI",
                        render: (r) => r.curp_cfdi || "—",
                      },
                    ]}
                  />
                </SectionCard>

                <SectionCard
                  title="Diferencias SDI / datos laborales"
                  icon={ClipboardList}
                  tone="critical"
                  testid="dash-confronta-sdi"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CountBadge value={confronta.diferencias_sdi?.total} tone="critical" />
                    <Link
                      to="/confronta-trabajadores-imss"
                      className="text-xs font-semibold text-[#0F2D5C] hover:underline"
                    >
                      Ir a cuatrimestrales IMSS →
                    </Link>
                  </div>
                  <TrabajadoresMiniTable
                    items={confronta.diferencias_sdi?.items}
                    extraColumns={[
                      {
                        key: "sdi_catalogo",
                        label: "SDI catálogo",
                        render: (r) => r.sdi_catalogo || "—",
                      },
                      {
                        key: "sdi_cfdi",
                        label: "SDI CFDI",
                        render: (r) => r.sdi_cfdi || "—",
                      },
                      {
                        key: "diferencia",
                        label: "Δ",
                        render: (r) => (r.diferencia != null ? String(r.diferencia) : "—"),
                      },
                    ]}
                  />
                </SectionCard>
              </>
            ) : null}

            <SectionCard
              title="Cuatrimestrales pendientes"
              icon={Info}
              tone="info"
              testid="dash-alert-cuatrimestrales"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CountBadge value={data.cuatrimestrales_pendientes?.total} tone="info" />
                <Link
                  to="/confronta-trabajadores-imss"
                  className="text-xs font-semibold text-[#0F2D5C] hover:underline"
                >
                  Ir a declaraciones cuatrimestrales →
                </Link>
              </div>
              <div className="overflow-x-auto border rounded-sm bg-white">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Obra</TableHead>
                      <TableHead>Periodo</TableHead>
                      <TableHead>Filas incompletas</TableHead>
                      <TableHead>Origen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.cuatrimestrales_pendientes?.items || []).map((row) => (
                      <TableRow key={`${row.contrato_id}-${row.origen}`}>
                        <TableCell>
                          {row.numero_contrato} — {row.razon_social_obra}
                        </TableCell>
                        <TableCell>{row.periodo_label}</TableCell>
                        <TableCell>
                          {row.filas_incompletas}/{row.filas_total}
                        </TableCell>
                        <TableCell className="text-xs">{row.origen}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {data.cuatrimestrales_pendientes?.total === 0 ? (
                <p className="text-sm text-slate-600">
                  Sin cuatrimestrales pendientes según las reglas actuales de validación.
                </p>
              ) : null}
            </SectionCard>
          </>
        ) : null}
        </div>
      </LaboralFlujoOperativo>

      <div className="px-8 max-w-6xl pb-8">
        <DespachoOperationalSummary />
      </div>
    </div>
  );
}
